import { AI_CHAT_CONNECTION } from '@/lib/constants';
import { appendStepDetail, upsertStep } from '@/lib/ai-chat-trace';
import { chatStreamURL } from '@/api/brainstorm';
import { ref, watch } from 'vue';
import { tenantIdOf } from '@/api/interceptors';
import { useStore } from 'vuex';

const RECONNECT_MS = 3000;
const CONNECTING_AFTER_MS = 300;

// The stream is held open only while an answer is awaited. A reload or a second tab opens
// its own and is sent the answer so far first; a dropped connection is reopened by the
// EventSource itself and starts over from that snapshot.
// The server sends how long the turn has run rather than when it started, so the browser's
// own clock places the start and a clock that is off does not skew the timer.
export default function useAiChatStream(chatId, pending, { onEnd = () => {} } = {}) {
  const store = useStore();
  const streamed = ref(null);
  const connection = ref(null);

  const onAnswer = (event) => {
    const { promptId, text = '', steps = [], elapsedMs = 0 } = JSON.parse(event.data);
    streamed.value = { promptId, text, steps, startedAt: Date.now() - elapsedMs };
    connection.value = null;
  };

  const onDelta = (event) => {
    const { promptId, text = '' } = JSON.parse(event.data);
    if (streamed.value?.promptId !== promptId) {
      return;
    }
    streamed.value = { ...streamed.value, text: streamed.value.text + text };
  };

  const onStep = (event) => {
    const { promptId, step } = JSON.parse(event.data);
    if (streamed.value?.promptId !== promptId) {
      return;
    }
    streamed.value = { ...streamed.value, steps: upsertStep(streamed.value.steps, step) };
  };

  const onStepDelta = (event) => {
    const { promptId, stepId, text = '' } = JSON.parse(event.data);
    if (streamed.value?.promptId !== promptId) {
      return;
    }
    streamed.value = { ...streamed.value, steps: appendStepDetail(streamed.value.steps, stepId, text) };
  };

  watch([chatId, pending], ([id, isPending], previous, onCleanup) => {
    streamed.value = null;
    connection.value = null;
    if (id === null || !isPending) {
      return;
    }

    connection.value = AI_CHAT_CONNECTION.syncing;
    const slow = setTimeout(() => {
      if (connection.value === AI_CHAT_CONNECTION.syncing) {
        connection.value = AI_CHAT_CONNECTION.connecting;
      }
    }, CONNECTING_AFTER_MS);

    let source = null;
    let reconnect = null;
    const open = () => {
      source = new EventSource(chatStreamURL(id, tenantIdOf(store)), { withCredentials: true });
      source.addEventListener('answer', onAnswer);
      source.addEventListener('delta', onDelta);
      source.addEventListener('step', onStep);
      source.addEventListener('stepDelta', onStepDelta);
      // the stored answer is on its way through vortex; the streamed text stands in until then
      source.addEventListener('end', (event) => {
        source.close();
        const { promptId, steps = [], elapsedMs = 0 } = JSON.parse(event.data);
        onEnd({ chatId: id, promptId, steps, elapsedMs });
      });
      // an EventSource reopens a dropped connection itself but gives up for good on an error
      // response, such as the 502 while brainstorm is redeployed
      source.addEventListener('error', () => {
        if (source.readyState === EventSource.CLOSED) {
          reconnect = setTimeout(open, RECONNECT_MS);
        }
      });
    };
    open();
    onCleanup(() => {
      clearTimeout(slow);
      clearTimeout(reconnect);
      source.close();
    });
  }, { immediate: true });

  return { streamed, connection };
}
